import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ClipboardCheck, Info, Loader2, CheckCircle, HelpCircle, Gift } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TombolaModal from '../components/TombolaModal';
import AssessmentIntroScreen from '../components/AssessmentIntroScreen';
import ResultsPreviewScreen from '../components/ResultsPreviewScreen';
import AssessmentQuestionScreen from '../components/AssessmentQuestionScreen';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { assessmentsAPI, sessionAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

// Humanistix icon-only component (uses the actual icon image)
const HumanistixIcon = ({ className = "w-6 h-6" }) => (
  <img 
    src="/humanistix-ai-logo.png" 
    alt="" 
    className={className}
  />
);

// NEW: Assessment dimensions based on meeting decisions
// 4 Categories: Strategie, Governance, Technologie & Data, Mensen & Change
// Questions are less technical, more business-oriented
// 6-point Likert scale with "Not Applicable" option
// NOTE: Categories are ordered to maximize completion rate - People & Change first (most relatable)
import { getAssessmentDimensions, getIndustries, getCompanySizes, getScoreLabels, getLevelLabel } from '../data/assessmentDimensions';


const Assessment = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const [currentDimension, setCurrentDimension] = useState(0);
  const [answers, setAnswers] = useState({});
  const [naAnswers, setNaAnswers] = useState({}); // Track "Not Applicable" answers
  const [userInfo, setUserInfo] = useState({
    companyName: '',
    industry: '',
    companySize: '',
    name: '',
    email: '',
    phone: '',
    jobTitle: '',
  });
  const [showIntro, setShowIntro] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false); // NEW: Show contact form after questions
  const [showTombola, setShowTombola] = useState(false); // NEW: Show tombola after contact form
  const [tombolaCompleted, setTombolaCompleted] = useState(false); // Track if tombola was played
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [checkingHistory, setCheckingHistory] = useState(false);
  const [otherText, setOtherText] = useState({}); // Track "Other" text inputs for multiselect questions
  
  // Anonymous session tracking
  const [sessionId, setSessionId] = useState(null);
  const sessionStarted = useRef(false);
  const [startedAt, setStartedAt] = useState(null); // Track when user started the quickscan

  // Get translated data
  const assessmentDimensions = getAssessmentDimensions(language);
  const industries = getIndustries(language);
  const companySizes = getCompanySizes(language);
  const scoreLabels = getScoreLabels(language);

  // Start anonymous session when user begins the quickscan
  const startAnonymousSession = async () => {
    if (sessionStarted.current) return;
    sessionStarted.current = true;
    
    // Set started timestamp
    const now = new Date().toISOString();
    setStartedAt(now);
    sessionStorage.setItem('quickscan_started_at', now);
    
    try {
      // Check for existing session in sessionStorage
      const existingSessionId = sessionStorage.getItem('quickscan_session_id');
      if (existingSessionId) {
        setSessionId(existingSessionId);
        // Also restore startedAt if available
        const existingStartedAt = sessionStorage.getItem('quickscan_started_at');
        if (existingStartedAt) {
          setStartedAt(existingStartedAt);
        }
        return;
      }
      
      // Start new session
      const response = await sessionAPI.startSession();
      setSessionId(response.session_id);
      sessionStorage.setItem('quickscan_session_id', response.session_id);
      console.log('Anonymous session started:', response.session_id);
    } catch (error) {
      console.error('Failed to start anonymous session:', error);
      // Non-blocking - continue without session tracking
    }
  };

  // Save progress after each category completion
  const saveProgress = async (categoryIndex, categoryId) => {
    if (!sessionId) return;
    
    try {
      // Format current answers for API
      const formattedAnswers = [];
      assessmentDimensions.forEach((dim) => {
        dim.questions.forEach((q) => {
          if (!dim.isQualification && (answers[q.id] !== undefined || naAnswers[q.id])) {
            formattedAnswers.push({
              question_id: q.id,
              dimension: dim.id,
              value: naAnswers[q.id] ? null : (answers[q.id] || null),
              not_applicable: naAnswers[q.id] || false,
            });
          }
        });
      });
      
      await sessionAPI.updateProgress(sessionId, {
        current_category: categoryId,
        current_category_index: categoryIndex,
        answers: formattedAnswers,
        na_answers: naAnswers,
      });
      
      console.log(`Progress saved: category ${categoryId} (${categoryIndex + 1}/${assessmentDimensions.length})`);
    } catch (error) {
      console.error('Failed to save progress:', error);
      // Non-blocking - continue without saving
    }
  };

  // Pre-fill user info and check for previous assessments
  useEffect(() => {
    if (user) {
      setUserInfo(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
      }));
      
      // Check if user has previous assessments - if so, skip intro
      const checkPreviousAssessments = async () => {
        setCheckingHistory(true);
        try {
          const myAssessments = await assessmentsAPI.getMyAssessments();
          if (myAssessments && myAssessments.length > 0) {
            // User has done assessments before, skip intro
            setShowIntro(false);
            // Scroll to top
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 50);
          }
        } catch (error) {
          console.error('Failed to check previous assessments:', error);
        } finally {
          setCheckingHistory(false);
        }
      };
      
      checkPreviousAssessments();
    }
  }, [user]);

  // Warn user before leaving if they have started the assessment
  useEffect(() => {
    const hasStarted = Object.keys(answers).length > 0;
    
    const handleBeforeUnload = (e) => {
      if (hasStarted && !showContactForm) {
        e.preventDefault();
        e.returnValue = language === 'nl' 
          ? 'Je voortgang gaat verloren. Weet je zeker dat je wilt vertrekken?'
          : 'Your progress will be lost. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [answers, showContactForm, language]);

  const totalDimensions = assessmentDimensions.length;
  const progress = ((currentDimension + 1) / totalDimensions) * 100;

  const handleAnswerChange = (questionId, value) => {
    // For Likert scale questions (1-6), store as numbers
    // For dropdown/select questions, keep as strings for proper Select component functionality
    const isLikertValue = typeof value === 'number' || (typeof value === 'string' && /^[1-6]$/.test(value) && questionId < 25);
    const storedValue = isLikertValue && typeof value === 'string' ? parseInt(value, 10) : value;
    setAnswers((prev) => ({ ...prev, [questionId]: storedValue }));
    // If setting a value, remove from NA
    if (value !== undefined) {
      setNaAnswers((prev) => {
        const newNa = { ...prev };
        delete newNa[questionId];
        return newNa;
      });
    }
  };

  const handleNaChange = (questionId, isNa) => {
    if (isNa) {
      setNaAnswers((prev) => ({ ...prev, [questionId]: true }));
      // Remove regular answer when marking as NA
      setAnswers((prev) => {
        const newAnswers = { ...prev };
        delete newAnswers[questionId];
        return newAnswers;
      });
    } else {
      setNaAnswers((prev) => {
        const newNa = { ...prev };
        delete newNa[questionId];
        return newNa;
      });
    }
  };

  const currentDimensionData = assessmentDimensions[currentDimension];
  
  // Check if all questions are answered (either with a value or NA)
  const allQuestionsAnswered = currentDimensionData?.questions.every(
    (q) => answers[q.id] !== undefined || naAnswers[q.id] === true
  );

  const handleNext = async () => {
    // Save progress for current category before moving to next
    const currentCat = currentDimensionData;
    if (sessionId && currentCat) {
      saveProgress(currentDimension, currentCat.id);
    }
    
    if (currentDimension < totalDimensions - 1) {
      setCurrentDimension((prev) => prev + 1);
      // Scroll to top with smooth behavior and ensure it works
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 50);
    } else {
      // All questions answered, show contact form instead of submitting directly
      setShowContactForm(true);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 50);
    }
  };

  const handlePrevious = () => {
    if (currentDimension > 0) {
      setCurrentDimension((prev) => prev - 1);
      // Scroll to top with smooth behavior and ensure it works
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 50);
    }
  };

  const submitAssessment = async () => {
    setSubmitting(true);
    setError('');

    try {
      // Format answers for API
      const formattedAnswers = [];
      assessmentDimensions.forEach((dim) => {
        dim.questions.forEach((q) => {
          if (!dim.isQualification) {
            // Ensure value is always an integer, not a string
            const rawValue = answers[q.id];
            const numericValue = naAnswers[q.id] ? null : (typeof rawValue === 'number' ? rawValue : parseInt(rawValue, 10) || 1);
            
            formattedAnswers.push({
              question_id: q.id,
              dimension: dim.id, // Use consistent ID instead of localized title
              value: numericValue,
              not_applicable: naAnswers[q.id] || false,
            });
          }
        });
      });

      // Extract qualification answers
      const qualificationDim = assessmentDimensions.find(d => d.isQualification);
      const qualificationAnswers = {};
      if (qualificationDim) {
        qualificationDim.questions.forEach((q) => {
          qualificationAnswers[q.id] = answers[q.id];
          // Include "other" text if present for multiselect questions
          if (q.type === 'multiselect' && otherText[q.id]) {
            qualificationAnswers[`${q.id}_other_text`] = otherText[q.id];
          }
        });
      }

      const response = await assessmentsAPI.create({
        name: userInfo.name,
        email: userInfo.email,
        company: userInfo.companyName,
        phone: userInfo.phone || null,
        job_title: userInfo.jobTitle || null,
        industry: userInfo.industry || null,
        company_size: userInfo.companySize || null,
        answers: formattedAnswers,
        qualification_answers: qualificationAnswers,
        started_at: startedAt || sessionStorage.getItem('quickscan_started_at') || null,
      });

      // Debug: log the request payload
      console.log('Submitted payload:', {
        name: userInfo.name,
        email: userInfo.email,
        company: userInfo.companyName,
        answers_count: formattedAnswers.length,
        qualification_answers: qualificationAnswers,
      });
      console.log('Quickscan API Response:', response);
      console.log('Overall Score:', response.overall_score);
      console.log('Dimension Scores:', response.dimension_scores);

      // Mark anonymous session as converted/completed
      if (sessionId) {
        try {
          await sessionAPI.completeSession(sessionId, response.id);
          sessionStorage.removeItem('quickscan_session_id');
          sessionStorage.removeItem('quickscan_started_at');
          console.log('Anonymous session converted to assessment:', response.id);
        } catch (err) {
          console.error('Failed to mark session as completed:', err);
          // Non-blocking
        }
      }

      // Mark assessment as just completed (prevents exit popup)
      sessionStorage.setItem('assessmentJustCompleted', 'true');

      // Navigate to results
      navigate('/quickscan/results', {
        state: {
          results: response,  // Pass the full response as results
          userInfo,
        },
      });
    } catch (err) {
      // Extract more specific error message
      let errorMessage = language === 'nl' 
        ? 'Er is iets misgegaan. Probeer het opnieuw.'
        : 'Something went wrong. Please try again.';
      
      if (err.response) {
        // Server responded with error
        const status = err.response.status;
        const detail = err.response.data?.detail;
        
        console.error('Quickscan submission error:', { 
          status, 
          detail, 
          data: err.response.data,
          requestPayload: {
            name: userInfo.name,
            email: userInfo.email,
            company: userInfo.companyName,
            industry: userInfo.industry,
            company_size: userInfo.companySize,
            job_title: userInfo.jobTitle,
          }
        });
        
        if (status === 422) {
          // Show more specific error if available
          if (detail && Array.isArray(detail)) {
            const fieldErrors = detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
            errorMessage = language === 'nl'
              ? `Validatiefout: ${fieldErrors}`
              : `Validation error: ${fieldErrors}`;
          } else if (typeof detail === 'string') {
            errorMessage = detail;
          } else {
            errorMessage = language === 'nl'
              ? 'Niet alle velden zijn correct ingevuld. Controleer uw gegevens.'
              : 'Not all fields are correctly filled. Please check your data.';
          }
        } else if (status === 500) {
          errorMessage = language === 'nl'
            ? 'Serverfout. Probeer het later opnieuw of neem contact op met support.'
            : 'Server error. Please try again later or contact support.';
        } else if (detail) {
          errorMessage = detail;
        }
      } else if (err.request) {
        // Network error
        errorMessage = language === 'nl'
          ? 'Geen verbinding met de server. Controleer uw internetverbinding.'
          : 'No connection to server. Please check your internet connection.';
        console.error('Network error:', err.request);
      } else {
        console.error('Quickscan submission error:', err.message);
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Intro screen
  if (showIntro) {
    return (
      <AssessmentIntroScreen
        language={language}
        onStart={() => {
          setShowIntro(false);
          startAnonymousSession();
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
          }, 50);
        }}
      />
    );
  }

  // NEW: Contact form screen - shown after all questions are answered
  if (showContactForm) {
    return (
      <ResultsPreviewScreen
        language={language}
        userInfo={userInfo}
        setUserInfo={setUserInfo}
        answers={answers}
        naAnswers={naAnswers}
        industries={industries}
        companySizes={companySizes}
        submitting={submitting}
        error={error}
        submitAssessment={submitAssessment}
        setShowContactForm={setShowContactForm}
        setShowTombola={setShowTombola}
        showTombola={showTombola}
        tombolaCompleted={tombolaCompleted}
        setTombolaCompleted={setTombolaCompleted}
        sessionId={sessionId}
        totalDimensions={assessmentDimensions.length}
        setCurrentDimension={setCurrentDimension}
      />
    );
  }

  // Question screen
  return (
    <AssessmentQuestionScreen
      language={language}
      assessmentDimensions={assessmentDimensions}
      scoreLabels={scoreLabels}
      currentDimension={currentDimension}
      setCurrentDimension={setCurrentDimension}
      answers={answers}
      naAnswers={naAnswers}
      otherText={otherText}
      setOtherText={setOtherText}
      handleAnswerChange={handleAnswerChange}
      handleNaChange={handleNaChange}
      handleNext={handleNext}
      handlePrevious={handlePrevious}
      submitting={submitting}
      error={error}
    />
  );
};

export default Assessment;
